import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getUserNotifications, markNotificationRead, markAllNotificationsRead } from '../api/notificationApi';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContextOptimized';

const NotificationItem = ({ notification, onRead }) => {
    const isRead = notification.isRead;

    return (
        <div
            className={`p-4 border-b border-gray-100 transition-colors ${!isRead ? 'bg-primary-50' : 'bg-white'}`}
            onClick={() => !isRead && onRead(notification._id)}
        >
            <div className="flex gap-3">
                <div className={`p-2 rounded-full h-fit flex-shrink-0 ${!isRead ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
                    <BellIcon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                        <h3 className={`font-semibold text-sm ${!isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                            {notification.title}
                        </h3>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                            {new Date(notification.createdAt).toLocaleDateString()}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-snug mb-2">
                        {notification.body}
                    </p>
                    {!isRead && (
                        <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 text-[10px] font-medium rounded-full">
                            New
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const NotificationsPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { showSuccess, showError } = useToast();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loginRequired, setLoginRequired] = useState(false);

    const fetchNotifications = useCallback(async (pageNum = 1, isRefresh = false) => {
        if (!isAuthenticated) {
            setLoginRequired(true);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setLoginRequired(false);
            const response = await getUserNotifications(pageNum, 20);

            if (isRefresh || pageNum === 1) {
                setNotifications(response.data);
            } else {
                setNotifications(prev => [...prev, ...response.data]);
            }

            setHasMore(response.data.length === 20);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            if (error.isAuthError) {
                setLoginRequired(true);
                setNotifications([]);
            } else {
                showError('Failed to load notifications');
            }
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, showError]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            showSuccess('All notifications marked as read');
        } catch (error) {
            showError('Failed to mark all as read');
        }
    };

    const handleRead = async (id) => {
        try {
            await markNotificationRead(id);
            setNotifications(prev => prev.map(n =>
                n._id === id ? { ...n, isRead: true } : n
            ));
        } catch (error) {
            // Fail silently for user experience, update UI anyway to feel responsive?
            // No, better to reflect server state.
            console.error('Failed to mark read', error);
        }
    };

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom && hasMore && !loading) {
            setPage(prev => prev + 1);
            fetchNotifications(page + 1);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col h-screen">
            {/* Header */}
            <div className="bg-white sticky top-0 z-10 shadow-sm px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
                    </button>
                    <h1 className="text-lg font-semibold text-gray-900">Notifications</h1>
                </div>

                {notifications.length > 0 && (
                    <button
                        onClick={handleMarkAllRead}
                        className="text-primary-600 text-sm font-medium hover:text-primary-700 flex items-center gap-1"
                    >
                        <CheckIcon className="w-4 h-4" /> Mark all read
                    </button>
                )}
            </div>

            {/* Content */}
            <div
                className="flex-1 overflow-y-auto"
                onScroll={handleScroll}
            >
                {loading && page === 1 ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                        <p className="text-gray-500">Loading notifications...</p>
                    </div>
                ) : loginRequired ? (
                    <div className="flex flex-col items-center justify-center h-[70vh] px-4 text-center">
                        <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4">
                            <BellIcon className="w-8 h-8 text-primary-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Please log in to see notifications</h3>
                        <p className="text-gray-500 max-w-xs mb-6">
                            Sign in to view order updates, offers, and alerts meant for your account.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/login', { state: { from: '/notifications' } })}
                            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                        >
                            Log in
                        </button>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[70vh] px-4 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <BellIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications yet</h3>
                        <p className="text-gray-500 max-w-xs">
                            We'll let you know when we have updates, offers, or news for you.
                        </p>
                    </div>
                ) : (
                    <div className="pb-4">
                        {notifications.map(notification => (
                            <NotificationItem
                                key={notification._id}
                                notification={notification}
                                onRead={handleRead}
                            />
                        ))}

                        {loading && page > 1 && (
                            <div className="p-4 text-center text-gray-500 text-sm">
                                Loading more...
                            </div>
                        )}

                        {!hasMore && notifications.length > 0 && (
                            <div className="p-8 text-center text-gray-400 text-sm">
                                No more notifications
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
